import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, AlertCircle, CheckCircle, Clock, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

interface CronJobHealth {
  job_name: string;
  health_status: 'healthy' | 'degraded' | 'critical' | null;
  last_run_at: string | null;
  seconds_since_last_run: number | null;
  success_count_24h: number;
  failed_count_24h: number;
  avg_duration_ms: number | null;
  max_duration_ms: number | null;
}

interface CronJobHeartbeat {
  id: string;
  job_name: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  services_checked: number;
  services_healthy: number;
  services_degraded: number;
  services_down: number;
  services_unknown: number;
  notifications_sent: number;
  error_message: string | null;
}

export function CronHealthDashboard() {
  const [isOpen, setIsOpen] = useState(false);
  
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['cron-health'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cron_job_health')
        .select('*')
        .eq('job_name', 'scheduled-monitor')
        .maybeSingle();
      
      if (error) throw error;
      return data as CronJobHealth | null;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: recentRuns, isLoading: runsLoading } = useQuery({
    queryKey: ['cron-heartbeat'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cron_job_heartbeat')
        .select('*')
        .eq('job_name', 'scheduled-monitor')
        .order('started_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data as CronJobHeartbeat[];
    },
    refetchInterval: 60000,
  });

  const getHealthBadge = (status: string | null) => {
    switch (status) {
      case 'healthy':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20"><CheckCircle className="w-3 h-3 mr-1" />Healthy</Badge>;
      case 'degraded':
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20"><AlertCircle className="w-3 h-3 mr-1" />Degraded</Badge>;
      case 'critical':
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/20"><AlertCircle className="w-3 h-3 mr-1" />Critical</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="outline" className="text-green-500 border-green-500/20">Success</Badge>;
      case 'failed':
        return <Badge variant="outline" className="text-red-500 border-red-500/20">Failed</Badge>;
      case 'running':
        return <Badge variant="outline" className="text-blue-500 border-blue-500/20">Running</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const successRate = healthData 
    ? ((healthData.success_count_24h / (healthData.success_count_24h + healthData.failed_count_24h)) * 100).toFixed(1)
    : '0';

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-4">
      <Card className="border-dashed">
        <CardHeader>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-muted-foreground" />
                <div className="text-left">
                  <CardTitle className="text-lg">System Monitoring Health</CardTitle>
                  <CardDescription>
                    {healthLoading || runsLoading ? 'Loading...' : `Last run ${healthData?.last_run_at ? formatDistanceToNow(new Date(healthData.last_run_at), { addSuffix: true }) : 'never'} • ${successRate}% success rate`}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {healthData && getHealthBadge(healthData.health_status)}
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
      </Card>

      <CollapsibleContent className="space-y-4">
        {/* Health Overview */}
        <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Cron Job Health Monitor
              </CardTitle>
              <CardDescription>Automated monitoring system status</CardDescription>
            </div>
            {healthData && getHealthBadge(healthData.health_status)}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last Run
              </p>
              <p className="text-lg font-semibold">
                {healthData?.last_run_at 
                  ? formatDistanceToNow(new Date(healthData.last_run_at), { addSuffix: true })
                  : 'Never'}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Success Rate (24h)
              </p>
              <p className="text-lg font-semibold">
                {successRate}%
                <span className="text-sm text-muted-foreground ml-2">
                  ({healthData?.success_count_24h || 0}/{(healthData?.success_count_24h || 0) + (healthData?.failed_count_24h || 0)})
                </span>
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Avg Duration</p>
              <p className="text-lg font-semibold">
                {healthData?.avg_duration_ms 
                  ? `${(healthData.avg_duration_ms / 1000).toFixed(1)}s`
                  : 'N/A'}
              </p>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Max Duration</p>
              <p className="text-lg font-semibold">
                {healthData?.max_duration_ms 
                  ? `${(healthData.max_duration_ms / 1000).toFixed(1)}s`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Executions</CardTitle>
          <CardDescription>Last 10 monitoring cycles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentRuns && recentRuns.length > 0 ? (
              recentRuns.map((run) => (
                <div 
                  key={run.id} 
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(run.status)}
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}
                      </span>
                    </div>
                    {run.error_message && (
                      <p className="text-xs text-red-500">{run.error_message}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-right">
                      <p className="text-muted-foreground">Duration</p>
                      <p className="font-medium">
                        {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : 'N/A'}
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-muted-foreground">Services</p>
                      <p className="font-medium">
                        <span className="text-green-500">{run.services_healthy}</span>
                        {run.services_degraded > 0 && <span className="text-yellow-500 ml-1">+{run.services_degraded}</span>}
                        {run.services_down > 0 && <span className="text-red-500 ml-1">+{run.services_down}</span>}
                      </p>
                    </div>

                    {run.notifications_sent > 0 && (
                      <div className="text-right">
                        <p className="text-muted-foreground">Alerts</p>
                        <p className="font-medium">{run.notifications_sent}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No execution history available
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}
